/* LSC Motion Lab — preview-theme features under evaluation (roadmap #2, #8, #9).
   Loads after lsc-motion.js. Same safety model: if GSAP is absent or anything
   here fails, the page behaves exactly as the base motion system left it. */
(function () {
  'use strict';
  if (!window.gsap || !window.ScrollTrigger) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  function q(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function whenReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ================= #8 Slab Flip Viewer =================
     PDPs whose product has a second media image (graded slabs shot front+back)
     get a 3D flip card over the main media. Functional interaction — works under
     reduced motion too (instant swap instead of a rotation). */
  var flipHosts = [];
  function buildFlip() {
    var items = q('.product__media-item');
    if (items.length < 2) return;
    var imgs = items.map(function (it) { return it.querySelector('img'); });
    if (!imgs[0] || !imgs[1]) return;
    /* mount OUTSIDE the modal-opener so the flip button never nests under (or
       gets mistaken for) Dawn's zoom toggle */
    var host = items[0].querySelector('.product-media-container') || items[0];
    if (host.querySelector('.lsc-flip-stage')) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var stage = document.createElement('div');
    stage.className = 'lsc-flip-stage';
    stage.setAttribute('aria-hidden', 'true');
    var card = document.createElement('div');
    card.className = 'lsc-flip-card';
    ['lsc-front', 'lsc-back'].forEach(function (face, i) {
      var f = document.createElement('div');
      f.className = 'lsc-face ' + face;
      var im = imgs[i].cloneNode(false);
      im.removeAttribute('id');
      im.loading = 'eager';
      f.appendChild(im);
      card.appendChild(f);
    });
    stage.appendChild(card);
    host.appendChild(stage);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lsc-flip-btn';
    btn.setAttribute('aria-pressed', 'false');
    btn.setAttribute('aria-label', 'Flip card to see the back');
    btn.innerHTML = '<span aria-hidden="true">&#10561;</span> Flip card';
    host.appendChild(btn);

    var flipped = false;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation(); /* never reach the zoom modal opener */
      flipped = !flipped;
      btn.setAttribute('aria-pressed', String(flipped));
      gsap.to(card, { rotationY: flipped ? 180 : 0, duration: REDUCE.matches ? 0 : 0.8, ease: 'power3.inOut', overwrite: 'auto' });
    });
    flipHosts.push(items[0]);
  }

  /* ================= #2 PDP Holo Tilt =================
     Desktop fine-pointer only, never under reduced motion. The media img (or the
     flip card, if #8 built one) tilts toward the cursor with a glare sweep —
     transform/opacity only, event-driven quickTo, zero rAF loops. */
  function buildTilt() {
    if (!FINE.matches || REDUCE.matches) return;
    q('.product__media-item').forEach(function (item) {
      var media = item.querySelector('.media') || item;
      var flipCard = media.querySelector('.lsc-flip-card');
      /* flip-enabled media: tilt the whole flip card on X only (Y belongs to the flip) */
      var target = flipCard || item.querySelector('img');
      if (!target || item._lscTilt) return;
      item._lscTilt = true;

      var glare = document.createElement('div');
      glare.className = 'lsc-glare';
      glare.setAttribute('aria-hidden', 'true');
      (flipCard ? flipCard.closest('.lsc-flip-stage').parentElement : media).appendChild(glare);

      gsap.set(media, { perspective: 800 });
      var rx = gsap.quickTo(target, 'rotationX', { duration: 0.35, ease: 'power2.out' });
      var ry = flipCard ? null : gsap.quickTo(target, 'rotationY', { duration: 0.35, ease: 'power2.out' });

      item.addEventListener('pointermove', function (e) {
        if (document.body.classList.contains('overflow-hidden')) return;
        var r = item.getBoundingClientRect();
        var nx = (e.clientX - r.left) / r.width - 0.5;
        var ny = (e.clientY - r.top) / r.height - 0.5;
        rx(-ny * 8);
        if (ry) ry(nx * 10);
        glare.style.setProperty('--gx', (nx * 100 + 50) + '%');
        glare.style.setProperty('--gy', (ny * 100 + 50) + '%');
        gsap.to(glare, { opacity: 0.35, duration: 0.25, overwrite: 'auto' });
      });
      item.addEventListener('pointerleave', function () {
        gsap.to(target, { rotationX: 0, rotationY: flipCard ? undefined : 0, duration: 0.5, ease: 'power3.out', overwrite: 'auto', onComplete: function () {
          if (!flipCard) gsap.set(target, { clearProps: 'transform' }); /* flip card keeps its rotationY state */
        } });
        gsap.to(glare, { opacity: 0, duration: 0.3, overwrite: 'auto' });
      });
      /* zoom modal needs a clean slate — Dawn's toggle ONLY, never the lab's own flip button */
      var opener = item.querySelector('.product__media-toggle:not(.lsc-flip-btn)');
      if (opener) opener.addEventListener('click', function () {
        gsap.killTweensOf(target);
        if (!flipCard) gsap.set(target, { clearProps: 'transform' });
        gsap.set(glare, { opacity: 0 });
      });
    });
  }

  /* ================= #9 Category headings — appear instantly (no entrance) =================
     Owner opted for NO entrance animation on the category headings. This previously
     ran the SplitText "ignition" (per-letter assemble): it was heavy (dozens of
     letter-elements each with a compositor layer + a multi-second rAF hold), the wide
     titles swept in from the far left, and — worst — it armed each heading INVISIBLE
     and only revealed it when a scroll-trigger fired, so a missed trigger left the
     header blank. We no longer split them. Instead we take these headings OUT of every
     scroll-reveal system (Dawn's animate--slide-in opacity:0.01 and the base engine's
     claim) and force them visible, so they simply render in place. The holo shimmer
     (holo-headings.js) still runs on the intact <h2>; its Liquid-authored inline
     --cat-color gold is preserved (never clearProps:'all'). */
  function buildIgnition() {
    if (!window.gsap) return;
    var DAWN = ['scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen', 'scroll-trigger--cancel'];
    q('.collection__title h2.title').forEach(function (h2) {
      var wrapper = h2.closest('.collection__title');
      if (!wrapper || h2._lscIgnite) return;
      h2._lscIgnite = true;
      /* drop any reveal trigger the base engine / Dawn attached to the wrapper or h2 */
      if (window.ScrollTrigger) {
        ScrollTrigger.getAll().forEach(function (st) {
          if (st.trigger === wrapper || st.trigger === h2) st.kill();
        });
      }
      gsap.killTweensOf(wrapper);
      gsap.killTweensOf(h2);
      [wrapper, h2].forEach(function (el) {
        DAWN.forEach(function (c) { el.classList.remove(c); });
      });
      h2.removeAttribute('data-cascade');
      /* clear ONLY reveal-related inline props (never 'all' — the Pokemon h2's gold
         --cat-color is a Liquid-authored inline style that clearProps:'all' would wipe) */
      gsap.set([wrapper, h2], { clearProps: 'opacity,visibility,transform,perspective' });
    });
  }

  whenReady(function () {
    try { buildFlip(); } catch (e) { /* isolated */ }
    try { buildTilt(); } catch (e) { /* isolated */ }
    try { buildIgnition(); } catch (e) { /* isolated */ }
  });
})();
